import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import styled from "styled-components";
import OpaqueLoading from "../../opaqueLoading/opaqueLoading";

const StyledDialogContent = styled(DialogContent)`padding: 16px;`;
const StyledDialog = styled(Dialog)``;
const ModalHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const GradeMasterModal = ({ onClose, status, fetchData, propData }) => {
  const isEdit = Boolean(propData);

  const [form, setForm] = useState({
    id: propData?.id || "",
    stateName: propData?.StateName?.toString() || "",
    district: propData?.District?.toString() || "",
    pincode: propData?.Pincode?.toString() || "",
  });

  const [loading, setLoading] = useState(false);

  // Auto-fetch district/state when adding new
useEffect(() => {
  const fetchPincodeDetails = async () => {
    if (!isEdit && form.pincode?.length === 6) {
      setLoading(true);
      try {
        const verified = await verifyPincode();
        if (verified) {
          setForm((prev) => ({
            ...prev,
            district: verified.district,
            stateName: verified.stateName,
          }));
          window.NotificationUtils.showSuccess("Pincode verified successfully");
        } else {
          setForm((prev) => ({ ...prev, district: "", stateName: "" }));
          window.NotificationUtils.showError("Invalid Pincode. Please check again.");
        }
      } catch (err) {
        console.error(err);
        window.NotificationUtils.showError("Error fetching details for this pincode.");
      } finally {
        setLoading(false);
      }
    }
  };

  fetchPincodeDetails();
}, [form.pincode, isEdit]);


  useEffect(() => {
    if (propData) {
      setForm({
        id: propData?.id || "",
        stateName: propData?.StateName?.toString() || "",
        district: propData?.District?.toString() || "",
        pincode: propData?.Pincode?.toString() || "",
      });
    }
  }, [propData]);

  const changeHandler = (fieldName, value) => {
    setForm((prevForm) => ({ ...prevForm, [fieldName]: value }));
  };

  const verifyPincode = async () => {
    if (form.pincode && form.pincode.length === 6) {
      setLoading(true);
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${form.pincode}`);
        const data = await res.json();

        if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
          const office = data[0].PostOffice[0];
          return {
            district: office.District,
            stateName: office.State,
          };
        } else {
          return null;
        }
      } catch (err) {
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    }
    return null;
  };

  const submitHandler = async () => {
    if (!form.pincode || form.pincode.length !== 6) {
      window.NotificationUtils.showError("Please enter a valid 6-digit pincode.");
      return;
    }

    setLoading(true);
    try {
      const verified = await verifyPincode();

      if (!verified) {
        window.NotificationUtils.showError("Invalid Pincode. Please check again.");
        return;
      }

      if (isEdit) {
        // ✅ In Edit mode, match entered district/state with verified
        if (
          verified.district.toLowerCase() !== form.district.toLowerCase() ||
          verified.stateName.toLowerCase() !== form.stateName.toLowerCase()
        ) {
          window.NotificationUtils.showError(
            "Entered District or State does not match Pincode."
          );
          return;
        }

        // ✅ Save update
        await window.Platform.database.updatePincodeFromAddress(form);
        window.NotificationUtils.showSuccess("Pincode updated successfully");
      } else {
        // ✅ Add new
        const result = await window.Platform.database.addAddress({
          ...form,
          district: verified.district,
          stateName: verified.stateName,
        });

        if (result?.status) {
          window.NotificationUtils.showSuccess(result?.message);
        } else {
          window.NotificationUtils.showError(result?.message);
          return;
        }
      }

      fetchData();
      onClose();
    } catch (err) {
      console.error(err);
      window.NotificationUtils.showError("Error saving pincode details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog disableEnforceFocus maxWidth="sm" fullWidth open>
      <StyledDialogContent>
        <ModalHeaderContainer>
          <Typography variant="h5">{isEdit ? "Edit Pincode" : "Add Pincode"}</Typography>
          <IconButton onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </ModalHeaderContainer>

        <Stack gap="20px" sx={{ p: 1 }}>
          {loading && <OpaqueLoading />}

          <TextField
            label="Enter Pincode"
            disabled={isEdit} // editable only in Add mode
            value={form.pincode}
            onChange={(e) => changeHandler("pincode", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 6 }}
          />

          <TextField
            label="District"
            value={form.district}
            onChange={(e) => changeHandler("district", e.target.value)}
            fullWidth
            InputProps={{ readOnly: !isEdit }}
          />

          <TextField
            label="State"
            value={form.stateName}
            onChange={(e) => changeHandler("stateName", e.target.value)}
            fullWidth
            InputProps={{ readOnly: !isEdit }}
          />

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              color="primary"
              variant="contained"
              onClick={submitHandler}
              disabled={!form.pincode || !form.district || !form.stateName}
            >
              {isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </Stack>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default GradeMasterModal;
